/**
 * loadScript - the single gate for any third-party script that is not strictly necessary.
 *
 * Usage:
 *   import { loadScript } from 'services/consent/loadScript';
 *   loadScript('analytics', 'https://example.com/analytics.js', { id: 'analytics-sdk', defer: true });
 *
 * Behavior:
 *   - Queues calls until consent state has been resolved.
 *   - Drops calls for categories the user has not consented to.
 *   - Re-flushes the queue automatically when consent changes (after accept/reject/withdraw).
 *   - Idempotent: a script with the same id (or same src) will not be injected twice.
 */

let currentConsent = null; // { necessary, analytics, marketing, personalization }
let resolved = false;
const queue = []; // [{ category, src, opts }]
const loaded = new Set();

function alreadyLoaded(id, src) {
    if (id && document.getElementById(id)) return true;
    if (src) {
        if (loaded.has(src)) return true;
        if (document.querySelector(`script[src="${src}"]`)) return true;
    }
    return false;
}

function injectNow(category, src, opts = {}) {
    if (!src) return false;
    if (alreadyLoaded(opts.id, src)) return true;
    const el = document.createElement('script');
    el.src = src;
    if (opts.id) el.id = opts.id;
    if (opts.async !== false) el.async = true;
    if (opts.defer) el.defer = true;
    if (opts.crossOrigin) el.crossOrigin = opts.crossOrigin;
    if (opts.integrity) el.integrity = opts.integrity;
    if (opts.type) el.type = opts.type;
    if (opts.attrs && typeof opts.attrs === 'object') {
        Object.entries(opts.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    }
    if (typeof opts.onLoad === 'function') el.addEventListener('load', opts.onLoad);
    if (typeof opts.onError === 'function') el.addEventListener('error', opts.onError);
    document.head.appendChild(el);
    loaded.add(src);
    return true;
}

function isAllowed(category) {
    if (!currentConsent) return false;
    if (category === 'necessary') return true;
    return !!currentConsent[category];
}

export function loadScript(category, src, opts = {}) {
    if (!category || !src) return;
    if (!resolved) {
        queue.push({ category, src, opts });
        return;
    }
    if (isAllowed(category)) injectNow(category, src, opts);
}

/** Called by ConsentProvider after consent is resolved or changed. */
export function _applyConsent(consent) {
    currentConsent = consent || null;
    resolved = true;
    if (!queue.length) return;
    const remaining = [];
    for (const item of queue) {
        if (isAllowed(item.category)) {
            injectNow(item.category, item.src, item.opts);
        } else {
            // Keep queued in case user changes their mind later
            remaining.push(item);
        }
    }
    queue.length = 0;
    queue.push(...remaining);
}

export default loadScript;
